// Import necessary functions and modules
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { uniq, isEmpty, toArray, isObject } from "lodash";
import { useArrayToggle } from "@/Utils/arrayToggle";
import { formatLabels } from "@/Utils/formatLabels";

/**
 * Defines the possible states for a node in the spaces and assets tree.
 *
 * @typedef {Object} States
 * @property {string} EMPTY - Represents an empty state (no items selected).
 * @property {string} SINGLE - Represents a state where a single item is selected.
 * @property {string} ALL - Represents a state where all items are selected.
 * @property {string} INTERMEDIATE - Represents a state where some, but not all, items are selected.
 */
export const States = Object.freeze({
    EMPTY: "empty",
    SINGLE: "single",
    ALL: "all",
    INTERMEDIATE: "intermediate",
});
/**
 * A composable function for managing a tree structure of spaces and assets.
 *
 * @param {Array} buildings - An array of building objects.
 * @param {string} type - The type of tree ('space' or 'asset' or 'asset_group').
 * @param {Object} modelValue - The initial value for the selection model.
 * @param {Function} emit - A function to emit events to the parent component.
 * @returns {Object} An object containing various methods and computed properties for managing the tree.
 */
export function useSpacesAndAssetsTree(buildings, type, modelValue, emit) {
    /**
     * Initializes the i18n translation function.
     */
    const { t } = useI18n();

    /**
     * Imports the toggleArrayItem function from the arrayToggle utility.
     */
    const { toggleArrayItem } = useArrayToggle();

    /**
     * Reactive reference for the current selection.
     *
     * This reference holds the current state of selected buildings and children.
     * It is initialized based on the provided modelValue:
     * - If modelValue is empty, it starts with empty arrays for buildings and children.
     * - If modelValue is provided, it uses the buildings and children from modelValue,
     *   ensuring they are converted to arrays using the toArray function.
     *
     * @type {Ref<{buildings: Array, children: Array}>}
     */
    const selection = ref(
        isEmpty(modelValue)
            ? {
                  buildings: [],
                  children: [],
              }
            : {
                  buildings: isObject(modelValue.buildings)
                      ? toArray(modelValue.buildings)
                      : modelValue.buildings,
                  children: isObject(modelValue.children)
                      ? toArray(modelValue.children)
                      : modelValue.children,
              }
    );

    /**
     * Reactive reference for the current search term.
     * Initialized as an empty string.
     */
    const searchTerm = ref("");

    /**
     * Reactive reference to determine if assets should be included.
     * Initialized as false.
     */
    const includeAssets = ref(false);

    /**
     * Computes the tree structure based on the provided buildings.
     *
     * @returns {Array} The computed tree structure of buildings and their children.
     */
    const data = computed(() => {
        let data = renderTree(buildings);

        return data;
    });

    /**
     * Computed property that returns the filtered tree based on the search term.
     *
     * @returns {Array} The filtered tree structure.
     */
    const tree = computed(() => {
        // If there's no search term, return the original data
        if (!searchTerm.value) {
            return data.value;
        }
        // Otherwise, filter the tree based on the search term
        return handleTreeSearch(data.value, searchTerm.value);
    });

    /**
     * Computed property that returns an array of space objects.
     *
     * @returns {Array} An array of space objects, each containing id, label, and type.
     *
     * This computed property flattens the buildings array and their children,
     * creating a new array of objects representing spaces. It includes
     * the building itself as a space, as well as all its children that are of type "space".
     *
     * Each object in the resulting array has the following structure:
     * - id: The unique identifier of the space
     * - label: The name of the space
     * - identifier: The unique identifier of the space
     * - type: Always "space" for this computed property
     * - buildingId: The ID of the building the space belongs to
     */
    const spaces = computed(() => {
        let data = buildings
            .flatMap((building) => [
                {
                    id: building.id,
                    label: building.name,
                    identifier: building.identifier,
                    type: "space",
                    buildingId: building.id,
                    thumbnailUrl: building.thumbnailUrl,
                },
                ...building.children.map((child) => ({
                    id: child.id,
                    label: child.name,
                    identifier: child.identifier,
                    type: child.type,
                    buildingId: child.tenantId,
                    thumbnailUrl: child.thumbnailUrl,
                })),
            ])
            .filter((option) => option.type === "space");

        return data;
    });

    /**
     * Computed property that returns an array of asset or asset_group objects.
     *
     * @returns {Array} An array of asset or asset_group objects, each containing id, label, type, spaceId, and parentId.
     *
     * This computed property flattens the buildings array and their children,
     * creating a new array of objects representing assets. It includes
     * the building itself as an asset, as well as all its children that are not of type "space".
     *
     * Each object in the resulting array has the following structure:
     * - id: The unique identifier of the asset
     * - label: The name of the asset
     * - identifier: The unique identifier of the asset
     * - type: The type of the asset (e.g., "asset", "asset_group")
     * - spaceId: The ID of the space the asset belongs to (if applicable)
     * - parentId: The ID of the parent asset or asset group (if applicable)
     * - buildingId: The ID of the building the asset belongs to
     */
    const assets = computed(() => {
        let data = buildings
            .flatMap((building) => [
                {
                    id: building.id,
                    label: building.name,
                    identifier: building.identifier,
                    type: "building",
                    buildingId: building.id,
                    thumbnailUrl: building.thumbnailUrl,
                },
                ...building.children.map((child) => ({
                    id: child.id,
                    label: child.name,
                    identifier: child.identifier,
                    type: child.type,
                    spaceId: child.spaceId,
                    parentId: child.parentId,
                    buildingId: child.tenantId,
                    thumbnailUrl: child.thumbnailUrl,
                })),
            ])
            .filter((option) => option.type !== "space");

        return data;
    });

    const assetGroups = computed(() => {
        let data = buildings
            .flatMap((building) => [
                {
                    id: building.id,
                    label: building.name,
                    identifier: building.identifier,
                    type: "building",
                    buildingId: building.id,
                    thumbnailUrl: building.thumbnailUrl,
                },
                ...building.children.map((child) => ({
                    id: child.id,
                    label: child.name,
                    identifier: child.identifier,
                    type: child.type,
                    spaceId: child.spaceId,
                    parentId: child.parentId,
                    buildingId: child.tenantId,
                    thumbnailUrl: child.thumbnailUrl,
                })),
            ])
            .filter((option) => option.type === "asset_group");

        return data;
    });

    /**
     * Renders a tree structure from the given buildings.
     *
     * @param {Array} buildings - An array of building objects to be rendered into a tree.
     * @returns {Array} An array of tree items representing the hierarchical structure of buildings and their children.
     */
    const renderTree = (buildings) => {
        return buildings.map((building) => {
            // Create the base item for each building
            const item = {
                name: building.name,
                identifier: building.identifier,
                id: building.id,
                building: true,
                children: [],
            };

            // Filter children based on the type
            const items = building.children.filter((child) => {
                if (type === "space" || type === "asset_group") {
                    return child.type === type;
                } else {
                    return child.type !== "space";
                }
            });

            // Create a structure to hold all items
            const structure = items.reduce((collection, child) => {
                collection[child.id] = {
                    ...child,
                    children: [],
                    building: false,
                };
                return collection;
            }, {});

            // Array to hold ungrouped assets
            const ungroupedAssets = [];

            // Organize items into the tree structure
            items.forEach((child) => {
                const node = structure[child.id];

                if (child.parentId !== null) {
                    // Add to parent's children if it has a parent
                    structure[child.parentId].children.push(node);
                } else if (type !== "space" && child.type === "asset") {
                    // Add to ungrouped assets if it's an asset without a parent
                    ungroupedAssets.push(node);
                } else {
                    // Add directly to building's children otherwise
                    item.children.push(node);
                }
            });

            // Add ungrouped assets to a special group if there are any
            if (type === "asset" && ungroupedAssets.length > 0) {
                item.children.unshift({
                    name: t("components.filters.spaces_or_assets.ungrouped"),
                    id: `ungrouped-${building.id}`,
                    building: false,
                    children: ungroupedAssets,
                });
            }

            return item;
        });
    };

    /**
     * Handles updating filters based on user interactions.
     *
     * @param {Object} param0 - The parameters object.
     * @param {Object} param0.node - The node being updated.
     * @param {string} param0.state - The new state of the node.
     */
    const handleUpdateFilters = ({ node, state }, multiple = true) => {
        // if multiple is false, reset previous selection
        if (!multiple) {
            selection.value.children = [];
            selection.value.buildings = node.buildingId
                ? [node.buildingId]
                : [];
        }

        // If the type is not "space", reset asset inclusion
        if (type !== "space") {
            includeAssets.value = false;
            emit("update:includeAssets", false);
        }

        /**
         * Updates the selection and emits changes.
         *
         * @param {Function} updateFunction - The function to update the selection.
         */
        const updateSelection = (updateFunction) => {
            updateFunction();

            if (includeAssets.value && type === "space") {
                handleIncludeAssets();
            } else {
                emit("update:modelValue", selection.value);
            }
        };

        /**
         * Updates the children array in the selection.
         *
         * @param {Array} ids - The IDs to add to the children array.
         */
        const updateChildren = (ids) => {
            selection.value.children = uniq([
                ...selection.value.children,
                ...ids.filter((id) => id !== node.id),
            ]);
        };

        // Define actions for different states
        const actions = {
            [States.EMPTY]: () =>
                toggleArrayItem(
                    node.building
                        ? selection.value.buildings
                        : selection.value.children,
                    node.id
                ),
            [States.SINGLE]: () =>
                updateChildren(getNodeIdWithAllChildrenIds(node)),
            [States.INTERMEDIATE]: () => {
                actions[States.EMPTY]();
                actions[States.SINGLE]();
            },
            [States.ALL]: () => {
                const ids = getNodeIdWithAllChildrenIds(node);
                if (node.building) {
                    toggleArrayItem(selection.value.buildings, node.id);
                }
                selection.value.children = selection.value.children.filter(
                    (id) => !ids.includes(id)
                );
            },
        };

        // Update the selection based on the current state
        updateSelection(() => actions[state]?.());
    };

    /**
     * Handles the inclusion of assets in the selection.
     * This function is called when the 'includeAssets' option is enabled for spaces.
     */
    const handleIncludeAssets = () => {
        // Get all space IDs that are either directly selected or included as children
        const spaceIds = spaces.value
            .filter(
                (space) =>
                    selection.value.buildings?.includes(space.id) ||
                    selection.value.children.includes(space.id)
            )
            .map((space) => space.id);

        // Get all asset IDs that belong to the selected spaces
        const assetIds = assets.value
            .filter((asset) => {
                return spaceIds.includes(asset.spaceId);
            })
            .map((asset) => asset.id);

        // Add the asset IDs to the selection if there are any
        if (!isEmpty(assetIds)) {
            selection.value.children = uniq(
                selection.value.children.concat(assetIds)
            );
        }

        /**
         * Checks if all children of an asset group are selected.
         * @param {string} assetId - The ID of the asset group to check.
         * @returns {boolean} - True if all children are selected, false otherwise.
         */
        const checkAllChildrenSelected = (assetId) => {
            const children = assets.value.filter(
                (child) => child.parentId === assetId
            );
            return children.every((child) => {
                if (child.type === "asset_group") {
                    return checkAllChildrenSelected(child.id);
                } else {
                    return (
                        selection.value.children.includes(child.id) ||
                        assetIds.includes(child.id)
                    );
                }
            });
        };

        /**
         * Recursively adds an asset group and its parent groups to the selection.
         * @param {string} assetId - The ID of the asset group to add.
         */
        const addAssetGroupToSelection = (assetId) => {
            if (!selection.value.children.includes(assetId)) {
                selection.value.children.push(assetId);
            }
            const parentAsset = assets.value.find(
                (asset) => asset.id === assetId
            );
            if (parentAsset && parentAsset.parentId) {
                addAssetGroupToSelection(parentAsset.parentId);
            }
        };

        // Check all asset groups and add them to the selection if all their children are selected
        assets.value.forEach((asset) => {
            if (
                asset.type === "asset_group" &&
                checkAllChildrenSelected(asset.id)
            ) {
                addAssetGroupToSelection(asset.id);
            }
        });

        // Get IDs of top-level asset groups that have all children selected
        const assetGroupIds = assets.value.reduce((acc, asset) => {
            if (asset.type === "asset_group" && asset.parentId === null) {
                if (checkAllChildrenSelected(asset.id)) {
                    acc.push(asset.id);
                }
            }
            return acc;
        }, []);

        // Add top-level asset group IDs to the selection if there are any
        if (!isEmpty(assetGroupIds)) {
            selection.value.children = uniq(
                selection.value.children.concat(assetGroupIds)
            );
        }

        // Emit an update event with the combined array of asset IDs and asset group IDs
        emit("update:assets", assetIds.concat(assetGroupIds));

        // Emit the updated selection
        emit("update:modelValue", selection.value);
    };

    /**
     * Handles clearing the selection based on the type (space or asset).
     *
     * For spaces:
     * - Clears all selected buildings.
     * - If includeAssets is true, clears all children.
     * - If includeAssets is false, removes only space IDs from children.
     *
     * For assets:
     * - Removes all asset IDs from children.
     * - If includeAssets is true, sets it to false and emits the update.
     *
     * Finally, emits the updated selection.
     */
    const handleClear = () => {
        if (type === "space") {
            selection.value.buildings = [];

            if (includeAssets.value) {
                selection.value.children = [];
                handleIncludeAssets();
            } else {
                selection.value.children = selection.value.children.filter(
                    (id) => !optionIds.value.has(id)
                );
            }
        } else {
            selection.value.children = selection.value.children.filter(
                (id) => !optionIds.value.has(id)
            );
            includeAssets.value = false;
            emit("update:includeAssets", false);
        }

        emit("update:modelValue", selection.value);
    };

    /**
     * Handles updating the 'includeAssets' state.
     *
     * @param {boolean} value - The new value for 'includeAssets'.
     *
     * This function:
     * 1. Inverts the provided value and assigns it to 'includeAssets'.
     * 2. If 'includeAssets' is now true, it calls 'handleIncludeAssets()'.
     * 3. Emits an update event with the new 'includeAssets' value.
     */
    const handleUpdateIncludeAssets = (value) => {
        includeAssets.value = !value;

        if (includeAssets.value) {
            handleIncludeAssets();
        }

        emit("update:includeAssets", includeAssets.value);
    };

    /**
     * Performs a tree search based on the given query.
     *
     * @param {Array} nodes - The array of nodes to search through.
     * @param {string} query - The search query.
     * @returns {Array} - The filtered array of nodes that match the query.
     *
     * This function:
     * 1. Converts the query to lowercase for case-insensitive comparison.
     * 2. Recursively searches through the tree structure.
     * 3. Matches nodes based on name or identifier.
     * 4. Returns a new tree structure with matching nodes and their ancestors.
     * 5. Sets 'shouldAutoOpen' to true for matched nodes for UI purposes.
     */
    const handleTreeSearch = (nodes, query) => {
        const lowercaseQuery = query.toLowerCase();

        return nodes.reduce((filteredNodes, node) => {
            const filteredChildren = handleTreeSearch(
                node.children || [],
                query
            );
            const matchesQuery =
                node.name.toLowerCase().includes(lowercaseQuery) ||
                (node.identifier &&
                    node.identifier.toLowerCase().includes(lowercaseQuery));

            if (matchesQuery || filteredChildren.length > 0) {
                filteredNodes.push({
                    ...node,
                    children: filteredChildren,
                    shouldAutoOpen: true,
                });
            }

            return filteredNodes;
        }, []);
    };

    /**
     * Retrieves the ID of the given node and all its children's IDs recursively.
     *
     * @param {Object} node - The node object to process.
     * @returns {Array} An array of node IDs, including the current node and all its descendants.
     *
     * This function:
     * 1. Initializes an array with the current node's ID (if it exists).
     * 2. Recursively processes all children of the node.
     * 3. Concatenates the IDs of all descendants with the current node's ID.
     * 4. Returns a flattened array of all collected IDs.
     */
    const getNodeIdWithAllChildrenIds = (node) => {
        const ids = node.id ? [node.id] : [];

        if (node.children?.length) {
            return ids.concat(
                node.children.flatMap(getNodeIdWithAllChildrenIds)
            );
        }

        return ids;
    };

    /**
     * Computed property that returns the appropriate options based on the type.
     *
     * @type {ComputedRef<Array>}
     * @returns {Array} An array of options, either spaces or assets.
     *
     * This computed property:
     * 1. Checks if the type is "space".
     * 2. If true, it returns the spaces.value array.
     * 3. If false, it returns the assets.value array.
     * 4. Updates reactively when the type, spaces, or assets change.
     */
    const options = computed(() => {
        return type === "space" ? spaces.value : assets.value;
    });

    /**
     * Computed property that returns a Set of all option IDs.
     *
     * @type {ComputedRef<Set<string|number>>}
     * @returns {Set} A Set containing the unique IDs of all options.
     *
     * This computed property:
     * 1. Maps over the options array to extract all IDs.
     * 2. Creates a new Set from these IDs for efficient lookup.
     * 3. Updates reactively when the options change.
     */
    const optionIds = computed(() => {
        return new Set(options.value.map((option) => option.id));
    });

    /**
     * Filters and returns the selected options based on the current selection.
     *
     * @param {Object} selection - The current selection object containing buildings and children.
     * @returns {Array} An array of selected options.
     *
     * This function:
     * 1. Filters the available options based on the current selection.
     * 2. For spaces, it includes options that are either in the buildings or children of the selection.
     * 3. For other types (e.g., assets), it only includes options that are in the children of the selection.
     */
    const selectedOptions = (selection) => {
        return options.value.filter((option) =>
            type === "space"
                ? selection.buildings?.includes(option.id) ||
                  selection.children.includes(option.id)
                : selection.children.includes(option.id)
        );
    };

    const selectionText = computed(() => {
        return formatLabels(selectedOptions(selection.value));
    });

    const searchByType = (type) => {
        const items =
            type === "space"
                ? spaces.value
                : assets.value.filter((item) => item.type === type);

        if (!searchTerm.value) {
            return items;
        }

        const lowerCaseSearchTerm = searchTerm.value.toLowerCase();
        return items.filter(
            (item) =>
                item.type === type &&
                (item.label.toLowerCase().includes(lowerCaseSearchTerm) ||
                    (item.identifier &&
                        item.identifier
                            .toLowerCase()
                            .includes(lowerCaseSearchTerm)))
        );
    };

    return {
        spaces,
        assets,
        assetGroups,
        selection,
        tree,
        searchTerm,
        searchByType,
        handleClear,
        handleUpdateFilters,
        handleUpdateIncludeAssets,
        handleIncludeAssets,
        selectionText,
        selectedOptions,
    };
}
